import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import { GlobalContext } from "../../utils/context/GlobalContextProvider.jsx";
import { useTranslation } from "react-i18next";
import { ProjectSubscriptionApi, SubscriptionApi } from "../../api/authority/generated/index.ts";
import BillingDataForm from './Billing/BillingDataForm.jsx';
import BillingModal from "./Billing/BillingModal.jsx";
import PlanName from "./Subscription/PlanName.jsx";
import toast from "react-hot-toast";
import ConfirmationModal from "./Subscription/ConfirmationModal.jsx";
import CurrentPlanBox from "./Subscription/CurrentPlanBox.jsx";
import StripeCardModalWithElements from "./Subscription/StripeCardModal.jsx";
import { useRenderNumber } from "../../api/utilities/useValueRenderers.js";

const fetchSubscriptions = (authorityConfig, projectId) => {
    const api = new ProjectSubscriptionApi(authorityConfig);
    const originalList = api.listProjectSubscriptions.bind(api);
    api.listProjectSubscriptions = () => originalList();
    return api.listProjectSubscriptions();
};

function ProjectSubscription({ project }) {
    const { getAuthorityConfiguration } = useContext(GlobalContext);
    const { t } = useTranslation();
    const [isAnnual, setIsAnnual] = useState(true);
    const [packages, setPackages] = useState([]);
    const [activePackage, setActivePackage] = useState(project.subscription?.currentPackage || null);
    const [billingAccount, setBillingAccount] = useState(project.billingAccount);
    const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const renderNumber = useRenderNumber();
    const currencySymbol = 'Kč';
    const toastOptions = { duration: 6000, position: 'bottom-right' };

    const authorityConfig = useMemo(() => {
        const config = getAuthorityConfiguration();
        return config;
    }, []);

    // Track initial mount and prevent double fetch in Strict Mode
    const isInitialMount = useRef(true);
    const fetchInitiatedRef = useRef(new Map());

    useEffect(() => {
        if (!project?.id) return;

        const fetchData = async () => {
            // On initial mount, only fetch if not already initiated (Strict Mode protection)
            if (isInitialMount.current && fetchInitiatedRef.current.has(project.id)) {
                return;
            }

            // Mark as initiated on initial mount
            if (isInitialMount.current) {
                fetchInitiatedRef.current.set(project.id, true);
            }

            // Set initial subscription state
            let newActivePackage = project.subscription?.currentPackage || null;
            if (project.subscription) {
                newActivePackage = project.subscription.nextPackage || project.subscription.currentPackage;
                setIsAnnual(
                    project.subscription.nextBillingCycle
                        ? project.subscription.nextBillingCycle === 'annual'
                        : project.subscription.billingCycle === 'annual'
                );
            }
            setActivePackage(newActivePackage);

            try {
                const response = await fetchSubscriptions(authorityConfig, project.id);
                let updatedPackages = [...response];
                if (newActivePackage && !updatedPackages.some(pkg => pkg.id === newActivePackage.id)) {
                    updatedPackages.unshift(newActivePackage);
                }
                setPackages(updatedPackages);
                if (!newActivePackage && updatedPackages.length > 0) {
                    setActivePackage(updatedPackages[0]);
                }
            } catch (error) {
                console.error("Error fetching subscriptions:", error);
            }
        };

        fetchData();

        // Mark initial mount as complete after first run
        if (isInitialMount.current) {
            isInitialMount.current = false;
        }
    }, [project?.id]);

    const getPrice = (plan) => {
        const pkg = packages.find(p => p.name === plan);
        return isAnnual ? renderNumber(pkg?.yearlyCzk) : renderNumber(pkg?.monthlyCzk);
    };

    const handlePaymentSuccess = () => {
        setSetupIntentClientSecret(null);
        window.location.reload();
    };

    const handleSelectPackage = (pkg) => {
        setSelectedPackage(pkg);
        setIsConfirmationModalOpen(true);
    };

    const handleConfirmSelection = () => {
        if (!project.billingAccount || !project.billingAccount.companyName) {
            setIsBillingModalOpen(true);
        } else {
            updateProjectSubscription(selectedPackage);
        }
        setIsConfirmationModalOpen(false);
    };

    const updateProjectSubscription = (pkg) => {
        const api = new SubscriptionApi(authorityConfig);
        api.changeSubscription({
            projectId: project.id,
            packageId: pkg.id,
            updateSubscriptionInput: { billingCycle: isAnnual ? 'annual' : 'monthly' }
        })
            .then(response => {
                if (response.setupIntentClientSecret !== null) {
                    setSelectedSubscription(response);
                    setSetupIntentClientSecret(response.setupIntentClientSecret);
                } else {
                    window.location.reload();
                }
            })
            .catch(error => {
                toast.error('Failed to change subscription.', toastOptions);
                console.error("Error updating subscription:", error);
            });
    };

    const PackageCard = ({ packageInfo, index }) => {
        const bgColors = ['bg-gray-500', 'bg-green-500', 'bg-sky-500', 'bg-violet-500'];
        const bgColorClass = bgColors[index % bgColors.length];
        return (
            <div className={`pkg-${packageInfo.name} relative col-span-full xl:col-span-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 shadow-sm rounded-b-lg`}>
                <div className={`absolute top-0 left-0 right-0 h-0.5 ${bgColorClass}`} aria-hidden="true"></div>
                <div className="px-5 pt-5 pb-6 border-b border-gray-200 dark:border-gray-700/60">
                    <header className="flex items-center mb-2">
                        <div className={`w-6 h-6 rounded-full shrink-0 mr-3 ${bgColorClass}`}>
                            <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
                                <path d="M12 17a.833.833 0 01-.833-.833 3.333 3.333 0 00-3.334-3.334.833.833 0 110-1.666 3.333 3.333 0 003.334-3.334.833.833 0 111.666 0 3.333 3.333 0 003.334 3.334.833.833 0 110 1.666 3.333 3.333 0 00-3.334 3.334c0 .46-.373.833-.833.833z"/>
                            </svg>
                        </div>
                        <h3 className="text-lg text-gray-800 dark:text-gray-100 font-semibold">
                            <PlanName planName={packageInfo.name} />
                        </h3>
                    </header>
                    <div className="text-gray-800 dark:text-gray-100 font-bold mb-4">
                        <span className="text-3xl">{getPrice(packageInfo.name)}</span>
                        <span className="text-2xl"> {currencySymbol}</span>
                        <span className="text-gray-500 font-medium text-sm">/{t('plan.month')}</span>
                    </div>
                    {activePackage?.id === packageInfo.id ? (
                        <button className="btn bg-indigo-500 text-white w-full">
                            {t('project.active')}
                        </button>
                    ) : (
                        <button className="btn bg-gray-900 text-white w-full" onClick={() => handleSelectPackage(packageInfo)}>
                            {t('project.select')}
                        </button>
                    )}
                </div>
                <div className="px-5 pt-4 pb-5">
                    <div className="text-xs text-gray-800 dark:text-gray-100 font-semibold uppercase mb-4">
                        {t('plan.whats-included')}:
                    </div>
                    <ul>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.users-count')}: <strong>{packageInfo.maxUsers === 0 ? t('plan.unlimited') : packageInfo.maxUsers}</strong>
                        </li>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.integrations-count')}: <strong>{packageInfo.maxIntegrations === 0 ? t('plan.unlimited') : packageInfo.maxIntegrations}</strong>
                        </li>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.free-domain-count')}: <strong>{packageInfo.maxDomains === 0 ? t('plan.unlimited') : packageInfo.maxDomains}</strong>
                        </li>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.updates-count')}: <strong>{(24 / packageInfo.dataUpdatesPerDay)}h</strong>
                        </li>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.discount-codes-evaluation')}: <FeatureYesNo question={packageInfo.allowDiscountCodeEvaluation} />
                        </li>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.influencer-evaluation')}: <FeatureYesNo question={packageInfo.allowInfluencerEvaluation} />
                        </li>
                        <li className="flex items-center py-1 text-sm gap-x-2">
                            {t('plan.affil-evaluation')}: <FeatureYesNo question={packageInfo.allowAffiliateEvaluation} />
                        </li>
                    </ul>
                </div>
            </div>
        );
    };

    const FeatureYesNo = ({ question }) => (
        question ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-green-500 mr-2" viewBox="0 0 24 24">
                <path d="M5 12l5 5l10 -10" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
        ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-red-500 mr-2" viewBox="0 0 24 24">
                <path d="M6 6l12 12" stroke="currentColor" strokeWidth="2" fill="none" />
                <path d="M18 6l-12 12" stroke="currentColor" strokeWidth="2" fill="none" />
            </svg>
        )
    );

    return (
        <WideSecureLayout title={t('route.subscription')}>
            <StripeCardModalWithElements
                isOpen={Boolean(setupIntentClientSecret?.length)}
                onClose={() => setSetupIntentClientSecret(null)}
                setupIntentClientSecret={setupIntentClientSecret}
                project={project}
                billingAccount={billingAccount}
                selectedPackage={selectedPackage}
                selectedSubscription={selectedSubscription}
                onPaymentSuccess={handlePaymentSuccess}
            />
            <BillingModal
                isOpen={isBillingModalOpen}
                onClose={() => setIsBillingModalOpen(false)}
                title={t('project.billing-data')}
            >
                <BillingDataForm
                    project={project}
                    onSave={(billingAccount) => {
                        setBillingAccount(billingAccount);
                        setIsBillingModalOpen(false);
                        updateProjectSubscription(selectedPackage);
                    }}
                />
            </BillingModal>
            <CurrentPlanBox project={project} currencySymbol={currencySymbol} />
            <ConfirmationModal
                isOpen={isConfirmationModalOpen}
                onConfirm={handleConfirmSelection}
                onClose={() => setIsConfirmationModalOpen(false)}
                project={project}
                packageInfo={selectedPackage}
                isAnnual={isAnnual}
            />
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl mb-8">
                <div className="p-6 space-y-6">
                    <section>
                        <div className="flex items-center space-x-3 mb-6 cursor-pointer" onClick={() => setIsAnnual(prev => !prev)}>
                            <div className={`text-sm ${!isAnnual ? 'text-gray-800 font-bold' : 'text-gray-500 font-medium'}`}>
                                {t('payment.monthly')}
                            </div>
                            <div className="form-switch">
                                <input
                                    type="checkbox"
                                    id="toggle"
                                    className="sr-only"
                                    checked={isAnnual}
                                    onChange={() => setIsAnnual(prev => !prev)}
                                />
                                <label className="bg-gray-400 dark:bg-gray-700" htmlFor="toggle">
                                    <span className="bg-white shadow-sm" aria-hidden="true"></span>
                                    <span className="sr-only">{t('payment.pay-annually')}</span>
                                </label>
                            </div>
                            <div className={`text-sm ${isAnnual ? 'text-gray-800 font-bold' : 'text-gray-500 font-medium'}`}>
                                {t('payment.annually')}
                                <span className="text-green-500 ml-1">(-30%)</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-6">
                            {packages.map((pkg, index) => (
                                <PackageCard key={pkg.id} packageInfo={pkg} index={index} />
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </WideSecureLayout>
    );
}

export default ProjectSubscription;